import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { submitReport } from "../utility/httpActions/reportActions";
import { trackEvent } from "../utility/analytics";

const REASONS_BY_ENTITY_TYPE = {
  PRODUCT: [
    { value: "SPAM", label: "Spam or misleading" },
    { value: "COUNTERFEIT", label: "Counterfeit or fake product" },
    { value: "INAPPROPRIATE_CONTENT", label: "Inappropriate content" },
    { value: "SCAM", label: "Possible scam" },
    { value: "OTHER", label: "Something else" },
  ],
  SHOP: [
    { value: "SCAM", label: "Scam operation" },
    { value: "FAKE_OR_IMPERSONATION", label: "Fake or impersonation" },
    { value: "POLICY_VIOLATION", label: "Policy violation" },
    { value: "HARASSMENT", label: "Harassment or abuse" },
    { value: "OTHER", label: "Something else" },
  ],
  USER: [
    { value: "SPAM", label: "Spam" },
    { value: "HARASSMENT", label: "Harassment or abuse" },
    { value: "FAKE_OR_IMPERSONATION", label: "Fake or impersonation" },
    { value: "SUSPICIOUS_ACTIVITY", label: "Suspicious activity" },
    { value: "OTHER", label: "Something else" },
  ],
};

const ReportDialog = ({ open, onClose, entityType, entityId, entityName }) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [description, setDescription] = useState("");
  const [snackbar, setSnackbar] = useState(null);

  const reasons = REASONS_BY_ENTITY_TYPE[entityType] ?? [];

  const handleDismiss = () => {
    setSelectedReason("");
    setDescription("");
    onClose();
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      submitReport({
        entityType,
        entityId,
        reason: selectedReason,
        description,
      }),
    onSuccess: () => {
      trackEvent("report_submitted", {
        entityType,
        entityId,
        reason: selectedReason,
      });
      handleDismiss();
      setSnackbar({
        severity: "success",
        message:
          "Thanks for the report. We'll review it and take action if needed.",
      });
    },
    onError: (error) => {
      if (error?.status === 409) {
        handleDismiss();
        setSnackbar({
          severity: "info",
          message: "You've already reported this.",
        });
      } else {
        setSnackbar({
          severity: "error",
          message: "Something went wrong. Please try again.",
        });
      }
    },
  });

  return (
    <>
      <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          Report {entityName || entityType.toLowerCase()}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Why are you reporting this?
          </Typography>
          <RadioGroup
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
          >
            {reasons.map((r) => (
              <FormControlLabel
                key={r.value}
                value={r.value}
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#046287" } }}
                  />
                }
                label={<Typography variant="body2">{r.label}</Typography>}
              />
            ))}
          </RadioGroup>
          <TextField
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            placeholder="Additional details (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 500 }}
            helperText={`${description.length}/500`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDismiss} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => mutate()}
            disabled={!selectedReason || isPending}
            sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#b71c1c" } }}
          >
            {isPending ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Submit Report"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default ReportDialog;
